import { useState, useEffect, useCallback } from 'react';
import { organizationCollectionService } from '../services/organizationCollectionService';

/**
 * Custom hook for fetching and managing organization-wide collection data
 * 
 * This hook provides a React-friendly interface to the organizationCollectionService,
 * handling loading states, error handling, and data caching. It follows the
 * service-oriented architecture pattern by wrapping the service with React-specific
 * state management.
 */
export const useOrganizationCollections = (organizationId, userId, userRole) => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch all collections for the organization with site counts
   * Uses useCallback to prevent unnecessary re-renders and infinite loops
   */
  const fetchCollections = useCallback(async () => {
    if (!organizationId || !userId) {
      setCollections([]);
      setError(null);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const collectionsData = await organizationCollectionService.getCollectionsWithSiteCounts(
        organizationId, 
        userId, 
        userRole
      );
      setCollections(collectionsData);
    } catch (err) {
      console.error("Error fetching organization collections:", err);
      setError(err.message || "Failed to fetch collections");
      setCollections([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId, userId, JSON.stringify(userRole)]);

  // Fetch collections when dependencies change
  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  return { 
    collections, 
    loading, 
    error, 
    refetch: fetchCollections 
  };
};

/**
 * Hook for getting just collection data without site counts (for faster loading)
 */
export const useOrganizationCollectionsOnly = (organizationId, userId, userRole) => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCollections = useCallback(async () => {
    if (!organizationId || !userId) {
      setCollections([]);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const collectionsData = await organizationCollectionService.getCollectionsForOrganization(
        organizationId, 
        userId, 
        userRole
      );
      setCollections(collectionsData);
    } catch (err) {
      console.error("Error fetching organization collections (no site counts):", err);
      setError(err.message || "Failed to fetch collections");
      setCollections([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId, userId, JSON.stringify(userRole)]);

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  return { 
    collections, 
    loading, 
    error, 
    refetch: fetchCollections 
  };
};